import { Api } from '@monorepo/shared/apiClient';
import {
  DocumentFoldersCacheEvents,
  documentFoldersCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/documentFoldersCacheRegister';
import {
  OrganizationDocumentsCacheEvents,
  organizationDocumentsCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/organizationDocumentsCacheRegister';
import {
  SiteDocumentsCacheEvents,
  siteDocumentsCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/siteDocumentsCacheRegister';
import { DocumentFolderWithFileCountResponse } from 'mapistry-shared';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type UseDocumentFoldersListParams = Partial<Api.FetchDocumentFoldersParams> & {
  config?: QueryConfig<
    DocumentFolderWithFileCountResponse[],
    Api.ErrorResponse
  >;
};

const KEY_START = 'document-folders-list';

export const createKey = ({ organizationId }: Api.FetchDocumentFoldersParams) =>
  [KEY_START, organizationId] as const;

const fetcher: Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchDocumentFolders
> = (_key, organizationId) => Api.fetchDocumentFolders({ organizationId });

export const useDocumentFoldersList = ({
  config: inputConfig,
  organizationId,
}: UseDocumentFoldersListParams) => {
  const isEnabled = !!organizationId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled ? createKey({ organizationId }) : undefined;
  const { data, ...queryInfo } = useQuery(key, fetcher, config);

  return {
    ...queryInfo,
    documentFolders: data,
  };
};

type InvalidateCacheParams = Api.FetchDocumentFoldersParams;

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([KEY_START, keyParams.organizationId]),
  );
}

documentFoldersCacheRegister(
  [
    DocumentFoldersCacheEvents.CREATE,
    DocumentFoldersCacheEvents.UPDATE,
    DocumentFoldersCacheEvents.DELETE,
  ],
  {
    hookName: 'useDocumentFoldersList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);

// Response contains the file count for each folder, which can be changed by create/delete/move.
organizationDocumentsCacheRegister(
  [
    OrganizationDocumentsCacheEvents.CREATE,
    OrganizationDocumentsCacheEvents.DELETE,
    OrganizationDocumentsCacheEvents.MOVE,
  ],
  {
    hookName: 'useDocumentFoldersList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);

siteDocumentsCacheRegister(
  [SiteDocumentsCacheEvents.CREATE, SiteDocumentsCacheEvents.DELETE],
  {
    hookName: 'useDocumentFoldersList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);
